/**
 * Écran « Budget 2027 — brouillon ».
 *
 * Brouillon personnel, réservé nominativement par le serveur. Trois temps :
 * la proposition salariale à soumettre au Bureau, avec les règles de la prime
 * de partage de la valeur ; l'arbitrage poste par poste à partir du
 * Simulateur ; la publication sur le Simulateur, en un clic après aperçu.
 *
 * Aucun montant ni texte chiffré n'est écrit ici : tout vient de l'API.
 */

import { euros, eurosCentimes, eurosSigne, nombre, pourcent } from '@scc/core';
import type { FormuleSalariale, LigneProposition, PosteBrouillon } from '@scc/core';
import {
  ajouterLigne2027,
  brouillon2027,
  choisirFormule2027,
  modifierLigne2027,
  publier2027,
  saisirPoste2027,
  supprimerLigne2027,
  type EtatBrouillon2027,
} from '../api.js';
import { h, vider } from '../dom.js';
import { badge, enteteSection, insight, kpi, noteLecture } from '../composants.js';

/** Montant saisi en français : espaces et virgule décimale admis. */
function lireMontant(texte: string): number | null {
  const brut = texte.replace(/[^\d,.-]/g, '').replace(',', '.');
  if (brut === '' || brut === '-') return null;
  const n = Number(brut);
  return Number.isFinite(n) ? n : null;
}

const ORIGINES: Record<PosteBrouillon['origine'], string> = {
  simulateur: 'Simulateur',
  saisi: 'saisi',
  detail: 'détaillé',
  proposition: 'proposition salariale',
};

export function vueBudget2027(apresPublication: () => void): HTMLElement {
  const zone = h('div', {}, h('p', { classe: 'note-lecture' }, 'Chargement du brouillon…'));
  const message = h('div', { classe: 'barre-actions', role: 'status', 'aria-live': 'polite' });

  const signaler = (texte: string): void => {
    vider(message);
    message.appendChild(h('span', { classe: 'titre' }, texte));
  };

  /** Exécute une modification, puis recharge le brouillon. */
  const agir = (action: Promise<unknown>, succes?: string): void => {
    void action
      .then(() => {
        if (succes) signaler(succes);
        charger();
      })
      .catch((e: unknown) => {
        signaler(e instanceof Error ? e.message : 'Modification refusée.');
      });
  };

  let confirmation = false;

  const charger = (): void => {
    void brouillon2027()
      .then((e) => {
        vider(zone);
        zone.appendChild(rendre(e));
      })
      .catch((e: unknown) => {
        vider(zone);
        zone.appendChild(
          insight('negative', '!', e instanceof Error ? e.message : 'Le brouillon n’a pas pu être chargé.'),
        );
      });
  };

  // ── 1. Proposition salariale ─────────────────────────────────────────────
  const tableauFormule = (e: EtatBrouillon2027, f: FormuleSalariale): HTMLElement => {
    const p = e.proposition;
    const t = p.totaux[f];
    const ligne = (s: LigneProposition): HTMLElement => {
      const r = s.formules[f];
      return h(
        'tr',
        {},
        h('th', { scope: 'row' }, s.libelle, h('br', {}), h('span', { classe: 'note-lecture' }, s.fonction)),
        h('td', { classe: 'num' }, pourcent(s.taux, 2)),
        h('td', { classe: 'num' }, eurosCentimes(s.brut2026)),
        h('td', { classe: 'num' }, eurosCentimes(r.brutMensuel)),
        h('td', { classe: 'num' }, eurosCentimes(r.brutAnnuel)),
        h('td', { classe: 'num' }, r.prime > 0 ? eurosCentimes(r.prime) : '—'),
        h('td', { classe: 'num' }, eurosCentimes(r.remunerationAnnuelle)),
        h(
          'td',
          { classe: 'num', title: s.tauxChargesConstate ? 'taux constaté sur les paies' : 'taux estimé' },
          `${eurosCentimes(r.charges)} (${pourcent(s.tauxCharges, 1)})`,
        ),
        h('td', { classe: 'num' }, eurosCentimes(r.coutEmployeur)),
      );
    };
    return h(
      'div',
      { classe: 'enveloppe-table' },
      h(
        'table',
        { classe: 'table-budget' },
        h('caption', {}, (e.formules.find((x) => x.cle === f)?.libelle ?? f) + ` — ${p.exercice}`),
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            ...[
              'Salarié',
              'Hausse',
              'Brut mensuel 2026',
              `Brut mensuel ${p.exercice}`,
              'Brut annuel',
              'Prime de partage',
              'Rémunération annuelle',
              'Charges patronales',
              'Coût employeur',
            ].map((x) => h('th', { scope: 'col' }, x)),
          ),
        ),
        h(
          'tbody',
          {},
          ...p.salaries.map(ligne),
          h(
            'tr',
            { classe: 'total' },
            h('th', { scope: 'row' }, 'Équipe'),
            h('td', {}, ''),
            h('td', {}, ''),
            h('td', {}, ''),
            h('td', { classe: 'num' }, eurosCentimes(t.brut)),
            h('td', { classe: 'num' }, eurosCentimes(t.primes)),
            h('td', { classe: 'num' }, eurosCentimes(t.brut + t.primes)),
            h('td', { classe: 'num' }, eurosCentimes(t.charges)),
            h('td', { classe: 'num' }, eurosCentimes(t.cout)),
          ),
        ),
      ),
    );
  };

  const detailCalculs = (e: EtatBrouillon2027): HTMLElement =>
    h(
      'div',
      { classe: 'text-block' },
      ...e.proposition.salaries.map((s) =>
        h(
          'details',
          {},
          h('summary', {}, `Détail du calcul — ${s.libelle}`),
          ...e.formules.map((f) =>
            h(
              'div',
              {},
              h('p', {}, h('strong', {}, f.libelle)),
              h('ol', {}, ...s.formules[f.cle].etapes.map((x) => h('li', {}, x))),
            ),
          ),
        ),
      ),
    );

  const sectionProposition = (e: EtatBrouillon2027): HTMLElement => {
    const p = e.proposition;
    return h(
      'section',
      {},
      enteteSection(1, `Proposition salariale ${p.exercice}`, p.reference),
      h(
        'div',
        { classe: 'kpi-grid' },
        kpi('neutral', pourcent(p.tauxReference, 2), null, `Hausse de référence — ${p.libelleReferent}`, {
          sens: 'neutre',
          texte: p.salaries
            .filter((s) => s.part !== 1)
            .map((s) => `${s.libelle} : ${pourcent(s.taux, 2)}`)
            .join(' · '),
        }),
        kpi('neutral', euros(p.totaux.primes.cout), null, 'Coût employeur avec primes', {
          sens: 'neutre',
          texte: `dont ${euros(p.totaux.primes.primes)} de primes`,
        }),
        kpi('warning', eurosSigne(p.surcoutBrut), null, 'Surcoût si tout passe sur le brut', {
          sens: 'up',
          texte: `${euros(p.totaux.brut.cout)} au total, chaque année`,
        }),
      ),
      tableauFormule(e, 'primes'),
      tableauFormule(e, 'brut'),
      noteLecture(
        'Charges patronales au taux **constaté sur les paies de chaque salarié** pour l’exercice en ' +
          'cours ; la prime de partage de la valeur n’en supporte pas sous le plafond d’exonération. ' +
          'La rémunération annuelle s’entend avant cotisations salariales.',
      ),
      detailCalculs(e),
    );
  };

  // ── 2. Règles de la prime de partage de la valeur ───────────────────────
  const sectionRegles = (e: EtatBrouillon2027): HTMLElement => {
    const p = e.proposition;
    const icone = { positive: '✓', warning: '!', negative: '!', info: 'ℹ' } as const;
    return h(
      'section',
      {},
      enteteSection(
        2,
        'Primes de partage de la valeur : ce que dit le droit',
        `Règles vérifiées le ${p.reglesVerifieesLe.split('-').reverse().join('/')} sur les sources officielles`,
      ),
      ...p.remarques.map((r) =>
        insight(r.ton, icone[r.ton], `**${r.titre}${/[?!.]$/.test(r.titre) ? '' : '.'}** ${r.texte}`),
      ),
      h(
        'div',
        { classe: 'text-block' },
        h('p', {}, h('strong', {}, 'Sources')),
        h(
          'ul',
          {},
          ...p.sources.map((s) =>
            h('li', {}, h('a', { href: s.url, target: '_blank', rel: 'noopener noreferrer' }, s.libelle)),
          ),
        ),
      ),
    );
  };

  // ── 3. Arbitrage poste par poste ─────────────────────────────────────────
  const champMontant = (valeur: number, libelle: string, surValide: (n: number | null) => void): HTMLElement =>
    h('input', {
      classe: 'champ-montant',
      type: 'text',
      inputmode: 'decimal',
      value: nombre(valeur, 0),
      'aria-label': libelle,
      surChange: (ev) => {
        surValide(lireMontant((ev.target as HTMLInputElement).value));
      },
    });

  const ligneDetail = (l: PosteBrouillon['lignes'][number], postes: EtatBrouillon2027['postesSaisissables']): HTMLElement => {
    const etat = { ...l };
    const enregistrer = (): void => {
      agir(
        modifierLigne2027(l.id, {
          poste: etat.poste,
          libelle: etat.libelle,
          montant: etat.montant,
          incertaine: etat.incertaine,
          commentaire: etat.commentaire,
        }),
      );
    };
    return h(
      'tr',
      { classe: 'ligne-detail' },
      h(
        'th',
        { scope: 'row' },
        h('input', {
          classe: 'ctrl-select',
          type: 'text',
          value: l.libelle,
          maxlength: '120',
          'aria-label': 'Libellé de la ligne',
          surChange: (ev) => {
            const v = (ev.target as HTMLInputElement).value.trim();
            if (v === '') return;
            etat.libelle = v;
            enregistrer();
          },
        }),
        l.commentaire ? h('span', { classe: 'note-lecture' }, ` ${l.commentaire}`) : null,
      ),
      h('td', {}, ''),
      h(
        'td',
        { classe: 'num' },
        champMontant(l.montant, `Montant de ${l.libelle}`, (n) => {
          if (n === null) return;
          etat.montant = n;
          enregistrer();
        }),
      ),
      h(
        'td',
        {},
        h(
          'label',
          {},
          h('input', {
            type: 'checkbox',
            checked: l.incertaine,
            surChange: (ev) => {
              etat.incertaine = (ev.target as HTMLInputElement).checked;
              enregistrer();
            },
          }),
          ' incertaine',
        ),
      ),
      h(
        'td',
        {},
        h(
          'select',
          {
            classe: 'ctrl-select',
            'aria-label': 'Poste de rattachement',
            surChange: (ev) => {
              etat.poste = (ev.target as HTMLSelectElement).value;
              enregistrer();
            },
          },
          ...postes.map((p) => h('option', { value: p.code, selected: p.code === l.poste }, p.libelle)),
        ),
        ' ',
        h(
          'button',
          {
            classe: 'btn-reset',
            type: 'button',
            surClick: () => {
              agir(supprimerLigne2027(l.id), `Ligne « ${l.libelle} » supprimée.`);
            },
          },
          'Supprimer',
        ),
      ),
    );
  };

  const lignePoste = (p: PosteBrouillon, e: EtatBrouillon2027): HTMLElement[] => {
    const ecart = p.montant - p.montantSimulateur;
    const editable = p.origine === 'simulateur' || p.origine === 'saisi';
    const cellule = editable
      ? champMontant(p.montant, `${p.libelle}, brouillon 2027`, (n) => {
          // Champ vidé : le poste revient au montant du Simulateur.
          agir(saisirPoste2027(p.code, n));
        })
      : h('strong', {}, euros(p.montant));
    const ligne = h(
      'tr',
      {},
      h('th', { scope: 'row' }, p.libelle),
      h('td', { classe: 'num' }, euros(p.montantSimulateur)),
      h('td', { classe: `num ${p.origine === 'simulateur' ? 'engendre' : ''}` }, cellule),
      h('td', { classe: 'num' }, Math.abs(ecart) < 0.5 ? '—' : eurosSigne(ecart)),
      h(
        'td',
        {},
        p.origine === 'simulateur'
          ? h('span', { classe: 'note-lecture' }, ORIGINES[p.origine])
          : badge(p.origine === 'saisi' ? 'vigilance' : 'neutre', ORIGINES[p.origine]),
        ' ',
        p.origine === 'saisi'
          ? h(
              'button',
              {
                classe: 'btn-reset',
                type: 'button',
                surClick: () => {
                  agir(saisirPoste2027(p.code, null));
                },
              },
              'Revenir au Simulateur',
            )
          : null,
      ),
    );
    return [ligne, ...p.lignes.map((l) => ligneDetail(l, e.postesSaisissables))];
  };

  const formulaireAjout = (e: EtatBrouillon2027): HTMLElement => {
    const poste = h(
      'select',
      { classe: 'ctrl-select', id: 'ajout-2027-poste' },
      ...e.postesSaisissables.map((p) =>
        h('option', { value: p.code }, `${p.libelle} (${p.nature === 'produit' ? 'produit' : 'charge'})`),
      ),
    );
    const libelle = h('input', { classe: 'ctrl-select', type: 'text', id: 'ajout-2027-libelle', maxlength: '120' });
    const montant = h('input', { classe: 'champ-montant', type: 'text', inputmode: 'decimal', id: 'ajout-2027-montant' });
    const incertaine = h('input', { type: 'checkbox', id: 'ajout-2027-incertaine' });
    const commentaire = h('input', { classe: 'ctrl-select', type: 'text', id: 'ajout-2027-commentaire', maxlength: '500' });
    return h(
      'div',
      { classe: 'text-block' },
      h('p', {}, h('strong', {}, 'Ajouter une ligne'), ' — une recette nouvelle, ou le détail d’un poste de charge'),
      h(
      'form',
      {
        classe: 'editeur-champs',
        surSubmit: (ev) => {
          ev.preventDefault();
          const m = lireMontant(montant.value);
          if (libelle.value.trim() === '' || m === null) {
            signaler('Indiquez un libellé et un montant.');
            return;
          }
          agir(
            ajouterLigne2027({
              poste: poste.value,
              libelle: libelle.value.trim(),
              montant: m,
              incertaine: incertaine.checked,
              commentaire: commentaire.value.trim() === '' ? null : commentaire.value.trim(),
            }),
            `Ligne « ${libelle.value.trim()} » ajoutée.`,
          );
        },
      },
      h('label', {}, 'Poste de rattachement', poste),
      h('label', {}, 'Libellé', libelle),
      h('label', {}, 'Montant annuel', montant),
      h('label', {}, 'Commentaire', commentaire),
      h('label', { classe: 'case' }, 'Incertaine (non acquise)', incertaine),
      h('button', { classe: 'btn-action', type: 'submit' }, 'Ajouter'),
      ),
    );
  };

  const sectionArbitrage = (e: EtatBrouillon2027): HTMLElement => {
    const b = e.brouillon;
    const totalLigne = (libelle: string, brouillon: number, simulateur: number, classe: string): HTMLElement =>
      h(
        'tr',
        { classe },
        h('th', { scope: 'row' }, libelle),
        h('td', { classe: 'num' }, euros(simulateur)),
        h('td', { classe: 'num' }, classe === 'resultat' ? eurosSigne(brouillon) : euros(brouillon)),
        h('td', { classe: 'num' }, Math.abs(brouillon - simulateur) < 0.5 ? '—' : eurosSigne(brouillon - simulateur)),
        h('td', {}, ''),
      );

    const ongletsFormule = h(
      'div',
      { classe: 'tabs', role: 'tablist', 'aria-label': 'Formule salariale retenue' },
      ...e.formules.map((f) =>
        h(
          'button',
          {
            classe: 'tab',
            type: 'button',
            role: 'tab',
            'aria-selected': f.cle === b.formule ? 'true' : 'false',
            surClick: () => {
              if (f.cle !== b.formule) agir(choisirFormule2027(f.cle), `Formule retenue : ${f.libelle.toLowerCase()}.`);
            },
          },
          f.libelle,
        ),
      ),
    );

    const ecartIncertain = b.totaux.resultat - b.totauxCertains.resultat;
    return h(
      'section',
      {},
      enteteSection(
        3,
        `Arbitrage ${b.exercice} poste par poste`,
        'Point de départ : le scénario Simulateur de la Construction budgétaire',
      ),
      b.simulateurAbsent
        ? insight('warning', '!', `Le Simulateur ne porte pas l’exercice ${b.exercice} : le brouillon part de zéro.`)
        : null,
      h('p', { classe: 'note-lecture' }, 'Charges de personnel calculées selon :'),
      ongletsFormule,
      h(
        'div',
        { classe: 'enveloppe-table' },
        h(
          'table',
          { classe: 'table-budget' },
          h('caption', { classe: 'invisible' }, `Brouillon du budget ${b.exercice}`),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              ...['Poste', 'Simulateur', 'Brouillon', 'Écart', 'Origine'].map((x) => h('th', { scope: 'col' }, x)),
            ),
          ),
          h(
            'tbody',
            {},
            h('tr', { classe: 'categorie' }, h('th', { scope: 'row', colspan: '5' }, 'Produits')),
            ...b.produits.flatMap((p) => lignePoste(p, e)),
            totalLigne('Total des produits', b.totaux.produits, b.simulateur.produits, 'total'),
            h('tr', { classe: 'categorie' }, h('th', { scope: 'row', colspan: '5' }, 'Charges')),
            ...b.charges.flatMap((p) => lignePoste(p, e)),
            totalLigne('Total des charges', b.totaux.charges, b.simulateur.charges, 'total'),
            totalLigne(`Résultat ${b.exercice}`, b.totaux.resultat, b.simulateur.resultat, 'resultat'),
            Math.abs(ecartIncertain) >= 0.5
              ? totalLigne(
                  'Résultat sans les lignes incertaines',
                  b.totauxCertains.resultat,
                  b.simulateur.resultat,
                  'resultat',
                )
              : null,
          ),
        ),
      ),
      noteLecture(
        'Un montant saisi dans le brouillon prime sur le Simulateur ; un poste détaillé prend la somme de ' +
          'ses lignes ; les charges de personnel suivent la proposition salariale. Rien de tout cela ne ' +
          'touche le Simulateur avant publication.',
      ),
      formulaireAjout(e),
    );
  };

  // ── 4. Publication ────────────────────────────────────────────────────────
  const sectionPublication = (e: EtatBrouillon2027): HTMLElement => {
    const c = e.changements;
    const exercice = e.brouillon.exercice;
    const incertain = e.brouillon.totaux.resultat - e.brouillon.totauxCertains.resultat;
    const bouton = h(
      'button',
      {
        classe: 'btn-action',
        type: 'button',
        disabled: c.length === 0,
        surClick: () => {
          if (!confirmation) {
            confirmation = true;
            charger();
            return;
          }
          confirmation = false;
          agir(
            publier2027().then(() => {
              apresPublication();
            }),
            `Brouillon publié : ${String(c.length)} poste(s) ${exercice} arbitré(s) dans le Simulateur.`,
          );
        },
      },
      confirmation ? `Confirmer la publication de ${String(c.length)} poste(s)` : 'Publier sur le Simulateur',
    );
    return h(
      'section',
      {},
      enteteSection(4, 'Publication sur le Simulateur', `Ce que l’équipe et le Conseil National verront en ${exercice}`),
      incertain >= 0.5 && c.length > 0
        ? insight(
            'warning',
            '!',
            `Les lignes incertaines sont publiées avec le reste : ${euros(incertain)} de résultat en dépendent. ` +
              'Supprimez-les avant de publier si elles ne doivent pas encore y figurer.',
          )
        : null,
      c.length === 0
        ? insight('positive', '✓', `Le Simulateur porte déjà le brouillon : aucun poste ${exercice} ne changerait.`)
        : h(
            'div',
            { classe: 'enveloppe-table' },
            h(
              'table',
              { classe: 'comp-table encadre-tableau' },
              h('caption', {}, `Postes ${exercice} qui changeront dans le Simulateur`),
              h(
                'thead',
                {},
                h('tr', {}, ...['Poste', 'Actuellement', 'Après publication', 'Écart'].map((x) => h('th', { scope: 'col' }, x))),
              ),
              h(
                'tbody',
                {},
                ...c.map((x) =>
                  h(
                    'tr',
                    {},
                    h('th', { scope: 'row' }, x.libelle),
                    h('td', { classe: 'num' }, euros(x.avant)),
                    h('td', { classe: 'num' }, euros(x.apres)),
                    h('td', { classe: 'num' }, eurosSigne(x.apres - x.avant)),
                  ),
                ),
              ),
            ),
          ),
      h(
        'div',
        { classe: 'barre-actions' },
        bouton,
        confirmation
          ? h(
              'button',
              {
                classe: 'btn-reset',
                type: 'button',
                surClick: () => {
                  confirmation = false;
                  charger();
                },
              },
              'Annuler',
            )
          : null,
      ),
      noteLecture(
        'Chaque poste publié devient un montant arbitré du Simulateur, journalisé. Pour revenir en arrière, ' +
          'saisir l’ancien montant dans la Construction budgétaire, ou « Effacer les arbitrages de ce ' +
          'scénario » (tous exercices du Simulateur). Le brouillon reste ouvert et peut être publié de nouveau.',
      ),
    );
  };

  const rendre = (e: EtatBrouillon2027): HTMLElement =>
    h(
      'div',
      {},
      sectionProposition(e),
      h('hr', { classe: 'divider' }),
      sectionRegles(e),
      h('hr', { classe: 'divider' }),
      sectionArbitrage(e),
      h('hr', { classe: 'divider' }),
      sectionPublication(e),
    );

  charger();

  return h(
    'div',
    { classe: 'page', id: 'page-budget-2027', role: 'region', 'aria-label': 'Budget 2027, brouillon' },
    h(
      'div',
      { classe: 'entete-page' },
      h(
        'div',
        {},
        h('h1', {}, 'Budget 2027 — brouillon personnel'),
        h(
          'p',
          { classe: 'chapeau' },
          'La proposition salariale à soumettre au Bureau, puis l’arbitrage du budget 2027 poste par poste. ' +
            'Page visible de vous seul ; rien n’atteint le Simulateur avant publication.',
        ),
      ),
    ),
    message,
    zone,
  );
}
